/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { Avatar, Popover, Select, Button, Tooltip } from "antd";
import { DownOutlined, InfoCircleOutlined } from "@ant-design/icons";

/**
 * Manager "Reply As" popover — Figma Frame 2748 (EAI-1530).
 *
 * Two sections:
 *  1. DEFAULT TO EVERY LEAD — a role Select (opportunity user types from
 *     getOpportunityUserTypes). Choosing a role saves the manager's default for
 *     ALL future replies; it resolves per lead to the assigned user of that role.
 *  2. THIS LEAD ONLY — the reply-as candidates for the current lead (Me → most
 *     recent → assigned users). Clicking one overrides the signer for THIS lead
 *     only; "Clear" restores the default. The active signer row is highlighted.
 *
 * Presentational only — data (role options, rows) and persistence are owned by
 * the parent (engagementActivity via useReplyAsPreferences / getOpportunityUserTypes).
 *
 * Props:
 *   currentSigner   {id, fullName, profilePic} — drives trigger + row highlight
 *   roleOptions     [{value,label}] opportunity user types for the active opp
 *   selectedRole    saved default role value | undefined
 *   onSelectRole    (value) => void — persist the default role
 *   roleLoading     bool — Select loading state
 *   leadRows        [{id, fullName, profilePic, subtitle, isMe, isRecent}]
 *   onSelectUser    (row) => void — per-lead override
 *   onClear         () => void — clear the per-lead override
 *   hasOverride     bool — enables Clear
 *   lang, disabled
 */
const AVATAR_COLORS = ["#69B1FF", "#85A5FF", "#5CDBD3", "#95DE64", "#FF85C0", "#FFA940"];
const colorFor = id => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];

const initialsOf = name =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join("");

const SignerAvatar = ({ signer, size = 28 }) => (
  <Avatar
    size={size}
    src={signer?.profilePic || undefined}
    style={{
      flexShrink: 0,
      fontSize: size >= 28 ? 10.5 : 9,
      fontWeight: 600,
      ...(signer?.profilePic ? {} : { backgroundColor: colorFor(signer?.id), color: "#FFFFFF" }),
    }}
  >
    {initialsOf(signer?.fullName)}
  </Avatar>
);

const SectionHeader = ({ label, tip, action }) => (
  <div className="flex items-center justify-between bg-[#F5F5F5] px-2 py-2">
    <div className="flex items-center gap-2">
      <span className="text-[12px] font-medium leading-5 text-[rgba(0,0,0,0.65)]">{label}</span>
      {tip && (
        <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tip}</span>}>
          <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
        </Tooltip>
      )}
    </div>
    {action}
  </div>
);

const ReplyAsSelector = ({
  currentSigner,
  roleOptions = [],
  selectedRole,
  onSelectRole = () => {},
  roleLoading = false,
  leadRows = [],
  onSelectUser = () => {},
  onClear = () => {},
  hasOverride = false,
  lang = {},
  disabled,
}) => {
  const [open, setOpen] = useState(false);

  // Selecting a lead user closes the popover; defer so it lands after antd's
  // own controlled-Popover click handling (which would otherwise re-open it).
  const handleSelectUser = row => {
    setTimeout(() => setOpen(false), 0);
    onSelectUser(row);
  };

  const content = (
    <div style={{ width: 400 }} data-testid="reply-as-control">
      {/* 1. DEFAULT TO EVERY LEAD */}
      <SectionHeader
        label={lang.replyAsDefaultEveryLead || "DEFAULT TO EVERY LEAD"}
        tip={
          lang.replyAsDefaultInfo ||
          "Set who your replies are sent as by default for all leads.\n\nIf the selected role isn't assigned to a lead, replies will be sent as the most recent user who contacted that lead."
        }
      />
      <div className="flex flex-col gap-2 p-5">
        <span className="text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
          {lang.replyAsSelectRole || "Select default"}
        </span>
        <Select
          value={selectedRole || undefined}
          onChange={onSelectRole}
          options={roleOptions}
          loading={roleLoading}
          placeholder={lang.replyAsSelectRolePlaceholder || "Select default"}
          suffixIcon={<DownOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.25)" }} />}
          className="w-full"
        />
      </div>

      {/* 2. THIS LEAD ONLY */}
      <SectionHeader
        label={lang.replyAsThisLeadOnly || "THIS LEAD ONLY"}
        tip={
          lang.replyAsThisLeadInfo ||
          "Set who your replies are sent as for this lead only. Your default rule remains unchanged."
        }
        action={
          // Only shown when a per-lead override is active — no disabled state.
          hasOverride ? (
            <Button
              type="link"
              size="small"
              onClick={onClear}
              className="!h-auto !px-1 !text-[14px]"
            >
              {lang.replyAsClear || "Clear"}
            </Button>
          ) : null
        }
      />
      <div className="flex max-h-[290px] flex-col gap-1 overflow-y-auto p-3">
        {leadRows.map(row => {
          const active = currentSigner && +row.id === +currentSigner.id;
          return (
            <div
              key={row.id}
              role="button"
              tabIndex={0}
              onClick={() => handleSelectUser(row)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") handleSelectUser(row);
              }}
              style={{ gap: 11 }}
              className={`flex cursor-pointer items-center rounded-lg p-1 focus:outline-none ${
                active ? "bg-[#E6F4FF]" : "hover:bg-[#F8FAFC]"
              }`}
            >
              <SignerAvatar signer={row} size={28} />
              <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-medium leading-[22px] text-[rgba(0,0,0,0.88)]">
                    {row.fullName}
                    {row.isMe ? ` ${lang.replyAsMeTag || "(Me)"}` : ""}
                  </div>
                  {row.subtitle && (
                    <div className="truncate text-[12px] leading-5 text-[rgba(0,0,0,0.65)]">
                      {row.subtitle}
                    </div>
                  )}
                </div>
                {row.isRecent && (
                  <span className="shrink-0 whitespace-nowrap text-[12px] leading-5 text-[rgba(0,0,0,0.88)]">
                    {lang.replyAsMostRecent || "Most Recent"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={setOpen}
      trigger="click"
      placement="topRight"
      overlayInnerStyle={{ padding: 0, borderRadius: 8, overflow: "hidden" }}
      content={content}
    >
      <button
        type="button"
        disabled={disabled}
        data-testid="reply-as-trigger"
        style={{ outline: "none", border: "none", background: "transparent" }}
        className={`flex items-center gap-1.5 text-[13px] focus:outline-none ${
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        }`}
      >
        <span className="text-[#94A3B8]">{lang.replyAs || "Reply as"}:</span>
        {currentSigner && <SignerAvatar signer={currentSigner} size={20} />}
        <span className="max-w-[150px] truncate font-medium text-[#334155]">
          {currentSigner?.fullName || lang.replyAsSelect || "Select"}
        </span>
        <DownOutlined style={{ fontSize: 10, color: "#64748B" }} />
      </button>
    </Popover>
  );
};

export default ReplyAsSelector;
